import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 30,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 30,
    paddingBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#2563eb',
  },
  companyInfo: {
    flexDirection: 'column',
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 5,
  },
  companyDetails: {
    fontSize: 10,
    color: '#6b7280',
    lineHeight: 1.4,
  },
  invoiceTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#2563eb',
  },
  invoiceInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
  },
  invoiceDetails: {
    flexDirection: 'column',
  },
  invoiceLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 5,
  },
  invoiceValue: {
    fontSize: 11,
    color: '#6b7280',
    marginBottom: 10,
  },
  customerInfo: {
    flexDirection: 'column',
  },
  customerLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 5,
  },
  customerValue: {
    fontSize: 11,
    color: '#6b7280',
    marginBottom: 5,
  },
  table: {
    marginBottom: 30,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
  },
  tableHeaderText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#374151',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  tableCell: {
    fontSize: 10,
    color: '#6b7280',
  },
  tableCellRight: {
    fontSize: 10,
    color: '#6b7280',
    textAlign: 'right',
  },
  totals: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginTop: 20,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: 200,
    marginBottom: 5,
  },
  totalLabel: {
    fontSize: 11,
    color: '#6b7280',
  },
  totalValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#374151',
  },
  grandTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: 200,
    paddingTop: 10,
    borderTopWidth: 2,
    borderTopColor: '#2563eb',
    marginTop: 10,
  },
  grandTotalLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  grandTotalValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#2563eb',
  },
  statusSection: {
    marginTop: 30,
    padding: 15,
    backgroundColor: '#f9fafb',
    borderRadius: 5,
  },
  statusTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 5,
  },
  statusValue: {
    fontSize: 11,
    color: '#6b7280',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 9,
    color: '#9ca3af',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 10,
  },
});

const InvoicePDF = ({ invoice, user, language = 'en' }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(
      language === 'fa' ? 'fa-IR' : 'en-US',
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'fa' ? 'fa-IR' : 'en-US').format(amount);
  };

  const getStatusText = (status) => {
    if (language === 'fa') {
      switch (status) {
        case 'paid': return 'پرداخت شده';
        case 'pending': return 'در انتظار پرداخت';
        case 'failed': return 'پرداخت ناموفق';
        default: return status;
      }
    } else {
      switch (status) {
        case 'paid': return 'Paid';
        case 'pending': return 'Pending Payment';
        case 'failed': return 'Payment Failed';
        default: return status;
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'failed': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>
              {language === 'fa' ? 'سازنده چت‌بات' : 'Chatbot Maker'}
            </Text>
            <Text style={styles.companyDetails}>
              {language === 'fa' 
                ? 'پلتفرم ساخت چت‌بات هوشمند\ninfo@chatbotmaker.com\n+98 21 1234 5678'
                : 'Smart Chatbot Creation Platform\ninfo@chatbotmaker.com\n+98 21 1234 5678'
              }
            </Text>
          </View>
          <Text style={styles.invoiceTitle}>
            {language === 'fa' ? 'فاکتور' : 'INVOICE'}
          </Text>
        </View>

        {/* Invoice Info */}
        <View style={styles.invoiceInfo}>
          <View style={styles.invoiceDetails}>
            <Text style={styles.invoiceLabel}>
              {language === 'fa' ? 'شماره فاکتور:' : 'Invoice Number:'}
            </Text>
            <Text style={styles.invoiceValue}>#{invoice.invoice_number}</Text>
            
            <Text style={styles.invoiceLabel}>
              {language === 'fa' ? 'تاریخ فاکتور:' : 'Invoice Date:'}
            </Text>
            <Text style={styles.invoiceValue}>{formatDate(invoice.created_at)}</Text>
            
            <Text style={styles.invoiceLabel}>
              {language === 'fa' ? 'تاریخ سررسید:' : 'Due Date:'}
            </Text>
            <Text style={styles.invoiceValue}>{formatDate(invoice.due_date)}</Text>
          </View>

          <View style={styles.customerInfo}>
            <Text style={styles.customerLabel}>
              {language === 'fa' ? 'مشتری:' : 'Bill To:'}
            </Text>
            <Text style={styles.customerValue}>{user?.name || 'N/A'}</Text>
            <Text style={styles.customerValue}>{user?.email || 'N/A'}</Text>
            <Text style={styles.customerValue}>
              {language === 'fa' ? 'شناسه کاربری:' : 'User ID:'} {user?.id || 'N/A'}
            </Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, { flex: 2 }]}>
              {language === 'fa' ? 'شرح خدمات' : 'Description'}
            </Text>
            <Text style={[styles.tableHeaderText, { flex: 1 }]}>
              {language === 'fa' ? 'مقدار' : 'Quantity'}
            </Text>
            <Text style={[styles.tableHeaderText, { flex: 1 }]}>
              {language === 'fa' ? 'قیمت واحد' : 'Unit Price'}
            </Text>
            <Text style={[styles.tableHeaderText, { flex: 1 }]}>
              {language === 'fa' ? 'مجموع' : 'Total'}
            </Text>
          </View>
          
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { flex: 2 }]}>
              {language === 'fa' ? 'شارژ کیف پول' : 'Wallet Deposit'}
            </Text>
            <Text style={[styles.tableCell, { flex: 1 }]}>1</Text>
            <Text style={[styles.tableCellRight, { flex: 1 }]}>
              {formatCurrency(invoice.amount)} {language === 'fa' ? 'ریال' : 'IRR'}
            </Text>
            <Text style={[styles.tableCellRight, { flex: 1 }]}>
              {formatCurrency(invoice.amount)} {language === 'fa' ? 'ریال' : 'IRR'}
            </Text>
          </View>
        </View>

        {/* Totals */}
        <View style={styles.totals}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>
              {language === 'fa' ? 'مجموع:' : 'Subtotal:'}
            </Text>
            <Text style={styles.totalValue}>
              {formatCurrency(invoice.amount)} {language === 'fa' ? 'ریال' : 'IRR'}
            </Text>
          </View>
          
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>
              {language === 'fa' ? 'مالیات:' : 'Tax:'}
            </Text>
            <Text style={styles.totalValue}>
              {formatCurrency(0)} {language === 'fa' ? 'ریال' : 'IRR'}
            </Text>
          </View>
          
          <View style={styles.grandTotal}>
            <Text style={styles.grandTotalLabel}>
              {language === 'fa' ? 'مجموع کل:' : 'Total:'}
            </Text>
            <Text style={styles.grandTotalValue}>
              {formatCurrency(invoice.amount)} {language === 'fa' ? 'ریال' : 'IRR'}
            </Text>
          </View>
        </View>

        {/* Status Section */}
        <View style={styles.statusSection}>
          <Text style={styles.statusTitle}>
            {language === 'fa' ? 'وضعیت پرداخت:' : 'Payment Status:'}
          </Text>
          <Text style={[styles.statusValue, { color: getStatusColor(invoice.status) }]}>
            {getStatusText(invoice.status)}
          </Text>
          {invoice.status === 'paid' && invoice.paid_at && (
            <Text style={styles.statusValue}>
              {language === 'fa' ? 'تاریخ پرداخت:' : 'Payment Date:'} {formatDate(invoice.paid_at)}
            </Text>
          )}
        </View>

        {/* Footer */}
        <Text style={styles.footer}>
          {language === 'fa' 
            ? 'این فاکتور توسط سیستم چت‌بات ساز تولید شده است.'
            : 'This invoice was generated by Chatbot Maker system.'
          }
        </Text>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
